/**
 * Parsed tool call types with well-typed arguments extracted from ProgressToolCall.args.
 *
 * Any agent can implement parsing for its tools here so that rendering code
 * gets structured access to tool-specific fields instead of raw args casts.
 */

import {ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import {
  ToolType,
  MCP_TOOL_NAME_SEPARATOR,
  isMCPTool,
  isToolType,
  parseMCPToolName,
} from 'llm/chat-library/tool-details/types';
import {assertNever} from 'util/Types';

// ============================================================================
// Base and MCP interfaces
// ============================================================================

export interface BaseParsedProgressToolCall extends ProgressToolCall {
  tool: ToolType | string;
}

export interface MCPToolCall extends ProgressToolCall {
  tool: `${string}${typeof MCP_TOOL_NAME_SEPARATOR}${string}`;
  serverName: string;
  originalToolName: string;
  args: Record<string, unknown>;
}

// ============================================================================
// Typed tool call interfaces
// ============================================================================

export interface SearchToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.SearchTool;
  query: string;
  objectTypes: Array<string>;
}

export interface GetWarehouseOverviewToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.GetWarehouseOverviewTool;
}

export interface QueryWarehouseToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.QueryWarehouseTool;
  query: string;
}

export interface ReadBenchlingDocumentToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ReadBenchlingDocumentTool;
  documentId: string;
}

export interface AttachmentToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.AttachmentTool;
  attachmentId: string;
}

export interface PublicDataSubAgentToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.PublicDataSubAgent;
  query: string;
}

export interface SearchNotebookEntriesToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.SearchNotebookEntries;
  queries: Array<string>;
  authoredBy?: Array<string>;
  createdAfter?: string;
  createdBefore?: string;
  modifiedAfter?: string;
  modifiedBefore?: string;
  reviewCompletedAfter?: string;
  reviewCompletedBefore?: string;
  reviewStatus?: string;
  reviewRole?: string;
  inProjectOrFolder?: string;
  inStudy?: string;
  offset: number;
}

export interface SearchSequencesToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.SearchSequencesTool;
  query: string;
}

export interface IdsToModelsToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.IdsToModelsTool;
  objectIds: Array<string>;
  includeRelevantItems: boolean;
}

export interface PlottingToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.PlottingTool;
  code: string;
}

export interface GenerateImageToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.GenerateImageTool;
  prompt: string;
}

export interface CustomCodeToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CustomCodeTool;
  code: string;
}

export interface RunCodeToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.RunCodeTool;
  code: string;
}

export interface ToolSearchToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ToolSearch;
  query: string;
}

export interface ThinkToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.Think;
}

export interface ResearchSubAgentToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ResearchSubAgent;
}

export interface CompleteAnswerToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CompleteAnswer;
}

export interface CompleteAnswerToolToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CompleteAnswerTool;
}

export interface DeepResearchToolToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.DeepResearchTool;
}

export interface LoadSkillToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.LoadSkill;
  skillId: string;
  skillName: string;
  filePath: string;
}

export interface ManageSkillToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ManageSkill;
  action: 'create' | 'edit';
  skillContent: string;
  skillFiles: Record<string, string>;
  skillId: string | null;
}

export interface CreateAnalysisPlanToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CreateAnalysisPlan;
}

/**
 * Union of all parsed tool call types.
 * Each member has a unique literal `tool` discriminant, enabling TypeScript
 * to narrow the type in switch statements without explicit casts.
 */
export type ParsedProgressToolCall =
  | SearchToolCall
  | GetWarehouseOverviewToolCall
  | QueryWarehouseToolCall
  | ReadBenchlingDocumentToolCall
  | SearchNotebookEntriesToolCall
  | SearchSequencesToolCall
  | AttachmentToolCall
  | PublicDataSubAgentToolCall
  | IdsToModelsToolCall
  | PlottingToolCall
  | GenerateImageToolCall
  | CustomCodeToolCall
  | RunCodeToolCall
  | ToolSearchToolCall
  | ThinkToolCall
  | ResearchSubAgentToolCall
  | CompleteAnswerToolCall
  | CompleteAnswerToolToolCall
  | DeepResearchToolToolCall
  | LoadSkillToolCall
  | ManageSkillToolCall
  | CreateAnalysisPlanToolCall
  | MCPToolCall;

// ============================================================================
// Parser
// ============================================================================

function toStringArray(value: unknown): Array<string> {
  if (Array.isArray(value)) {
    return value as Array<string>;
  }
  if (typeof value === 'string' && value.length > 0) {
    return [value];
  }
  return [];
}

/**
 * Parse a generic ProgressToolCall into a typed ParsedProgressToolCall.
 * Returns null for tool types that don't have a known parser.
 */
export function parseProgressToolCall(toolCall: ProgressToolCall): ParsedProgressToolCall | null {
  const toolName = toolCall.tool;

  if (isMCPTool(toolName)) {
    const {serverName, originalToolName} = parseMCPToolName(toolName);
    return {
      ...toolCall,
      tool: toolName as MCPToolCall['tool'],
      serverName,
      originalToolName,
      args: toolCall.args ?? {},
    };
  }

  if (!isToolType(toolName)) {
    return null;
  }

  const args = toolCall.args ?? {};

  switch (toolName) {
    case ToolType.SearchTool:
      return {
        ...toolCall,
        tool: ToolType.SearchTool,
        query: (args.query as string) || '',
        objectTypes: (args.objectTypes as Array<string>) || [],
      };
    case ToolType.SearchNotebookEntries:
      return {
        ...toolCall,
        tool: ToolType.SearchNotebookEntries,
        queries: toStringArray(args.search_queries),
        authoredBy: args.authored_by as Array<string> | undefined,
        createdAfter: args.created_after as string | undefined,
        createdBefore: args.created_before as string | undefined,
        modifiedAfter: args.modified_after as string | undefined,
        modifiedBefore: args.modified_before as string | undefined,
        reviewCompletedAfter: args.review_completed_after as string | undefined,
        reviewCompletedBefore: args.review_completed_before as string | undefined,
        reviewStatus: args.review_status as string | undefined,
        reviewRole: args.review_role as string | undefined,
        inProjectOrFolder: args.in_project_or_folder as string | undefined,
        inStudy: args.in_study as string | undefined,
        offset: (args.offset as number) || 0,
      };
    case ToolType.SearchSequencesTool:
      return {...toolCall, tool: ToolType.SearchSequencesTool, query: (args.query as string) || ''};
    case ToolType.GetWarehouseOverviewTool:
      return {...toolCall, tool: ToolType.GetWarehouseOverviewTool};
    case ToolType.QueryWarehouseTool:
      return {...toolCall, tool: ToolType.QueryWarehouseTool, query: (args.query as string) || ''};
    case ToolType.ReadBenchlingDocumentTool:
      return {
        ...toolCall,
        tool: ToolType.ReadBenchlingDocumentTool,
        documentId: (args.document_id as string) || '',
      };
    case ToolType.AttachmentTool:
      return {
        ...toolCall,
        tool: ToolType.AttachmentTool,
        attachmentId: (args.attachmentId as string) || '',
      };
    case ToolType.PublicDataSubAgent:
      return {...toolCall, tool: ToolType.PublicDataSubAgent, query: (args.query as string) || ''};
    case ToolType.IdsToModelsTool:
      return {
        ...toolCall,
        tool: ToolType.IdsToModelsTool,
        objectIds: (args.object_ids as Array<string>) || [],
        includeRelevantItems: (args.include_relevant_items as boolean) || false,
      };
    case ToolType.PlottingTool:
      return {...toolCall, tool: ToolType.PlottingTool, code: (args.code as string) || ''};
    case ToolType.GenerateImageTool:
      return {...toolCall, tool: ToolType.GenerateImageTool, prompt: (args.prompt as string) || ''};
    case ToolType.CustomCodeTool:
      return {
        ...toolCall,
        tool: ToolType.CustomCodeTool,
        code: (args.code as string) || '',
      };
    case ToolType.RunCodeTool:
      return {
        ...toolCall,
        tool: ToolType.RunCodeTool,
        code: (args.code as string) || '',
      };
    case ToolType.ToolSearch:
      return {...toolCall, tool: ToolType.ToolSearch, query: (args.query as string) || ''};
    case ToolType.Think:
      return {...toolCall, tool: ToolType.Think};
    case ToolType.ResearchSubAgent:
      return {...toolCall, tool: ToolType.ResearchSubAgent};
    case ToolType.CompleteAnswer:
      return {...toolCall, tool: ToolType.CompleteAnswer};
    case ToolType.CompleteAnswerTool:
      return {...toolCall, tool: ToolType.CompleteAnswerTool};
    case ToolType.DeepResearchTool:
      return {...toolCall, tool: ToolType.DeepResearchTool};
    case ToolType.LoadSkill:
      return {
        ...toolCall,
        tool: ToolType.LoadSkill,
        skillId: (args.skill_id as string) || '',
        skillName: (args.skill_name as string) || '',
        filePath: (args.file_path as string) || '',
      };
    case ToolType.ManageSkill: {
      const rawSkillFiles = args.skill_files;
      const skillFiles: Record<string, string> =
        rawSkillFiles != null && typeof rawSkillFiles === 'object' && !Array.isArray(rawSkillFiles)
          ? Object.fromEntries(
              Object.entries(rawSkillFiles as Record<string, unknown>).map(([k, v]) => [
                k,
                typeof v === 'string' ? v : String(v ?? ''),
              ])
            )
          : {};
      const rawAction = args.action;
      const action: 'create' | 'edit' = rawAction === 'create' || rawAction === 'edit' ? rawAction : 'create';
      return {
        ...toolCall,
        tool: ToolType.ManageSkill,
        action,
        skillContent: (args.skill_content as string) || '',
        skillFiles,
        skillId: (args.skill_id as string) || null,
      };
    }

    case ToolType.CreateAnalysisPlan:
      return {...toolCall, tool: ToolType.CreateAnalysisPlan};

    // N/A - this tool is not found in TOOLS_TO_DISPLAY.
    case ToolType.Respond:
      return null;

    // TODO: Parse these displayed tools to support input/output rendering in the activity modal.
    case ToolType.ReadTargetDocument:
    case ToolType.ReadDraftDocument:
    case ToolType.AskQuestion:
    case ToolType.InstantiateDocument:
    case ToolType.SearchEntriesAndTemplates:
    case ToolType.SearchBenchlingObjects:
    case ToolType.TranscribeFile:
    case ToolType.ReviewTool:
    case ToolType.EditTableCells:
    case ToolType.UpdateDocument:
    case ToolType.InsertDocumentContent:
    case ToolType.SearchFiles:
    case ToolType.SearchSchemas:
    case ToolType.FillTables:
    case ToolType.ReadFiles:
    case ToolType.CreateLookupConfig:
      return null;
    default:
      assertNever(toolName);
      return null;
  }
}
